import { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingSpinner } from '@/components/atoms/primitives';
import { classNames } from '@/lib/utils';
import { useMapStore } from '@/stores/slices/useMapStore';
import type { PhaseState } from './MapPhaseIndicator.types';

/**
 * Global, non-blocking status indicator for map phases and key layers.
 * Renders a pill at the top of the map showing loading or error messages
 * (e.g. "Loading ports…", "Unable to load vessels…").
 * Uses LoadingSpinner atom and follows 8pt grid system.
 */
export function MapPhaseIndicator() {
  const mapLoading = useMapStore((state) => state.mapLoading);
  const initialLoadComplete = useMapStore((state) => state.initialLoadComplete);
  const layerStatus = useMapStore((state) => state.layerStatus);

  // Track dismissed errors to prevent them from showing again
  const [dismissedErrors, setDismissedErrors] = useState<Set<string>>(new Set());
  const autoDismissTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const phase = useMemo<PhaseState | null>(() => {
    // Initial map style / base loading
    if (mapLoading && !initialLoadComplete) {
      return {
        message: 'Loading map style…',
        tone: 'default',
        showSpinner: true,
      };
    }

    // Ordered priority of interesting, user-visible layers.
    // Analytics / base layers run fully in the background and are not treated
    // as blocking once the initial style is ready.
    const priorityLoading: Array<{ key: string; label: string }> = [
      { key: 'vessels', label: 'Loading vessels in selected area…' },
      { key: 'incidents', label: 'Loading incidents…' },
      { key: 'ports', label: 'Loading ports…' },
      { key: 'military-locations', label: 'Loading military locations…' },
    ];

    for (const entry of priorityLoading) {
      const status = layerStatus[entry.key];
      if (status?.status === 'loading') {
        return {
          message: entry.label,
          tone: 'default',
          showSpinner: true,
        };
      }
    }

    // Priority errors – show a specific red message for the first one we find.
    const priorityErrors: Array<{ key: string; label: string }> = [
      { key: 'vessels', label: 'Unable to load vessels for this region. Please try again.' },
      { key: 'incidents', label: 'Unable to load incidents. Please try again.' },
      { key: 'ports', label: 'Unable to load ports. Please try again.' },
      { key: 'military-locations', label: 'Unable to load military locations. Please try again.' },
    ];

    for (const entry of priorityErrors) {
      const status = layerStatus[entry.key];
      if (status?.status === 'error' && !dismissedErrors.has(entry.key)) {
        return {
          message: entry.label,
          tone: 'error',
          showSpinner: false,
          layerKey: entry.key,
        };
      }
    }

    // If nothing is actively loading but at least one layer had an error,
    // surface a generic, non-blocking hint in red.
    const hasError = Object.values(layerStatus).some((s) => s.status === 'error');
    if (hasError && !dismissedErrors.has('generic-error')) {
      return {
        message: 'Some layers failed to load. Retrying in background…',
        tone: 'error',
        showSpinner: false,
        layerKey: 'generic-error',
      };
    }

    return null;
  }, [mapLoading, initialLoadComplete, layerStatus, dismissedErrors]);

  // Auto-dismiss error messages after 8 seconds
  useEffect(() => {
    if (phase?.tone === 'error' && phase.layerKey) {
      // Clear any existing timeout
      if (autoDismissTimeoutRef.current) {
        clearTimeout(autoDismissTimeoutRef.current);
      }

      // Set new timeout to dismiss after 8 seconds
      autoDismissTimeoutRef.current = setTimeout(() => {
        if (phase?.layerKey) {
          setDismissedErrors((prev) => {
            const next = new Set(prev);
            next.add(phase.layerKey as string);
            return next;
          });
        }
      }, 8000);

      return () => {
        if (autoDismissTimeoutRef.current) {
          clearTimeout(autoDismissTimeoutRef.current);
        }
      };
    } else {
      // Clear timeout if not an error or no layerKey
      if (autoDismissTimeoutRef.current) {
        clearTimeout(autoDismissTimeoutRef.current);
        autoDismissTimeoutRef.current = null;
      }
    }
  }, [phase?.tone, phase?.layerKey]);

  // Reset dismissed errors when layer status changes from error to something else
  useEffect(() => {
    const currentErrorKeys = Object.entries(layerStatus)
      .filter(([_, status]) => status.status === 'error')
      .map(([key]) => key);

    const hasAnyErrors = currentErrorKeys.length > 0;

    setDismissedErrors((prev) => {
      const next = new Set(prev);
      // Remove keys that are no longer in error state
      prev.forEach((key) => {
        if (key === 'generic-error') {
          // Clear generic-error dismissal if there are no more errors
          if (!hasAnyErrors) {
            next.delete(key);
          }
        } else if (!currentErrorKeys.includes(key)) {
          // Remove specific layer error dismissals if that layer is no longer in error
          next.delete(key);
        }
      });
      return next;
    });
  }, [layerStatus]);

  const handleDismiss = () => {
    if (phase?.layerKey) {
      setDismissedErrors((prev) => {
        const next = new Set(prev);
        next.add(phase.layerKey as string);
        return next;
      });
      // Clear any pending auto-dismiss timeout
      if (autoDismissTimeoutRef.current) {
        clearTimeout(autoDismissTimeoutRef.current);
        autoDismissTimeoutRef.current = null;
      }
    }
  };

  if (!phase) return null;

  const { message, tone, showSpinner } = phase;

  return (
    <div
      className={classNames(
        'map-phase-indicator absolute top-3 left-1/2 -translate-x-1/2 z-20 py-1.5 px-3.5 rounded-full flex items-center gap-2 text-xs text-gray-200 shadow-[0_8px_20px_rgba(0,0,0,0.45)]',
        tone === 'error' ? 'bg-red-700/95 pointer-events-auto' : 'bg-slate-900/90 pointer-events-none',
      )}
    >
      {showSpinner && <LoadingSpinner size="sm" />}
      <span>{message}</span>
      {tone === 'error' && (
        <button
          type="button"
          onClick={handleDismiss}
          onMouseDown={(e) => e.stopPropagation()}
          className="bg-transparent border-none text-gray-200 cursor-pointer p-0 ml-1 flex items-center justify-center w-4 h-4 rounded-full text-sm leading-none opacity-80 transition-opacity duration-200 hover:opacity-100 hover:bg-white/20"
          aria-label="Dismiss error"
        >
          ×
        </button>
      )}
    </div>
  );
}
